import React, { Fragment } from 'react';
import classnames from 'classnames';
import { IAttendeeOrganizationDTO } from '../../../api/attendees.dao';
import { MultiSelectOption } from './_multi-select-option';

interface IProps {
    items: IAttendeeOrganizationDTO[];
    values: IAttendeeOrganizationDTO[];
    disabledItems: IAttendeeOrganizationDTO[];
    onChange: (value: IAttendeeOrganizationDTO[]) => void;
    isWide: boolean;
    isWalkIn?: boolean;
    organizationFollowsAttendees?: boolean;
}

const isAttendeeIncludesInOrganization = (attendee, organization) => {
    const attendeeValues = organization?.attendees || [];
    return attendeeValues.length ? attendeeValues
        .map(el => el.attendeeId)
        .includes(attendee.attendeeId) : false;
};

const isEveryAttendeeDisabled = (organization, disabledOrganization) => {
    return !!disabledOrganization
        && (organization.attendees ?? []).every(attendee => isAttendeeIncludesInOrganization(attendee, disabledOrganization));
};

const MultiSelectOrganizationList = (props: IProps) => {
    const { items, values, onChange, disabledItems, isWalkIn, organizationFollowsAttendees } = props;
    const dropsEmptyOrganizations = isWalkIn || organizationFollowsAttendees;

    const onOrganizationToggle = (organization, newValue: boolean) => {
        if (newValue) {
            const otherOrganizations = values.filter(el => el.organizationId !== organization.organizationId);
            onChange([...otherOrganizations, organization]);
        } else {
            onChange(values.filter(el => el.organizationId !== organization.organizationId));
        }
    };

    const onAttendeeToggle = (organization, selectedOrganization, attendee, newValue: boolean) => {
        if (newValue) {
            if (selectedOrganization) {
                const otherOrganizations = values.filter(el => el.organizationId !== organization.organizationId);
                const changedAttendees = [...selectedOrganization.attendees, attendee];
                const changedOrganization = { ...selectedOrganization, attendees: changedAttendees };
                onChange([...otherOrganizations, changedOrganization]);
            } else {
                onChange([...values, { ...organization, attendees: [attendee] }]);
            }
        } else {
            const otherOrganizations = values.filter(el => el.organizationId !== organization.organizationId);
            const changedAttendees = selectedOrganization.attendees.filter(el => el.attendeeId !== attendee.attendeeId);
            const changedOrganization = { ...selectedOrganization, attendees: changedAttendees };
            if (dropsEmptyOrganizations && !changedAttendees.length) {
                onOrganizationToggle(changedOrganization, false);
            } else {
                onChange([...otherOrganizations, changedOrganization]);
            }
        }
    };

    return (
        <div className={classnames('multi-select-list', { 'multi-select-list--wide': props.isWide })}>
            {items.map(organization => {
                if (isWalkIn && !organization.attendees?.length) return null;

                const selectedOrganization = values.find(el => el.organizationId === organization.organizationId);
                const disabledOrganization = disabledItems.find(el => el.organizationId === organization.organizationId);

                const isOrganizationSelected = !!selectedOrganization;
                const isOrganizationDisabled = organizationFollowsAttendees
                    ? isEveryAttendeeDisabled(organization, disabledOrganization)
                    : !!disabledOrganization;
                const isOrganizationOptionSelected = isOrganizationSelected || isOrganizationDisabled;

                return (
                    <Fragment key={organization.organizationId}>
                        <MultiSelectOption
                            isSelected={isOrganizationOptionSelected}
                            isDisabled={isOrganizationDisabled}
                            isGroup={true}
                            onClick={() => onOrganizationToggle(organization, !isOrganizationSelected)}
                            name={organization.name}
                        />
                        {organization.attendees?.map(attendee => {
                            const isAttendeeSelected = isAttendeeIncludesInOrganization(attendee, selectedOrganization);
                            const isAttendeeDisabled = isAttendeeIncludesInOrganization(attendee, disabledOrganization);
                            const isAttendeeOptionSelected = isAttendeeSelected || isAttendeeDisabled;

                            return (
                                <MultiSelectOption
                                    key={`attendee_${attendee.attendeeId}`}
                                    isSelected={isAttendeeOptionSelected}
                                    isDisabled={isAttendeeDisabled}
                                    isInsideGroup={true}
                                    onClick={() => onAttendeeToggle(organization, selectedOrganization, attendee, !isAttendeeSelected)}
                                    name={`${attendee.firstName} ${attendee.lastName}`}
                                />
                            );
                        })}
                    </Fragment>
                );
            })}
        </div>
    );
};

export { MultiSelectOrganizationList };

